import { PREMIUM_SIZES, PERGOLA_FX, PERGOLA_MARKUP, OPTION_MARKUP, OLD_QUOTE_FACTOR, SCREEN_USD_PER_FOOT } from '../utils/pergolaCatalog';
import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { fetchGoogleAdsData, SETUP_INSTRUCTIONS } from '../services/googleAdsApi';
import { fetchGA4Data } from '../services/googleAnalyticsApi';
import { BulkPricingField, BulkPricingMode, applyBulkPricingAdjustment } from './adminPricingUtils';
import {
  InvoiceDraft,
  InvoicePayment,
  InvoiceRecord,
  buildInvoiceDraftFromExistingInvoice,
  buildInvoiceDraftFromInvoice,
  buildInvoiceDraftFromQuote,
  buildInvoicePrintHtml,
  buildManualInvoiceDraft,
  buildScheduleLabels,
  calculateInvoicePaymentSchedule,
  calculateInvoiceTotals,
  deriveInvoiceStatus,
  emptyInvoiceLineItem,
  getInvoicePaymentRates,
  getInvoicePayments,
  normalizeInvoiceLineItems,
  normalizeLineItem,
} from './adminInvoiceUtils';

interface QuoteSubmission {
  id: number;
  created_at: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  action: string;
  // Address
  street_address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  // Product
  pergola_model?: string;
  pergola_size?: string;
  pergola_color?: string;
  pergola_location?: string;
  selected_addons?: string[];
  // Pricing
  base_price?: number;
  addons_total?: number;
  install_cost?: number;
  delivery_fee?: number;
  fall_sale_discount?: number;
  subtotal?: number;
  hst?: number;
  total_with_hst?: number;
  total_min?: number;
  total_max?: number;
  // Lead details
  timeline?: string;
  budget?: string;
  message?: string;
  product_type?: string;
  status?: string;
  referrer?: string;
}

interface FinancingInquiry {
  id: number;
  created_at: string;
  name: string;
  email: string;
  phone: string;
  total_amount: number;
  financing_amount: number;
  status: string;
}

interface QuickCallback {
  id: number;
  created_at: string;
  phone: string;
  source: string;
  status: string;
  best_time: string;
}

interface MarketingMetrics {
  totalLeads: number;
  conversionRate: number;
  avgQuoteValue: number;
  topSources: { source: string; count: number; value: number }[];
  dailyLeads: { date: string; count: number }[];
  sourceBreakdown: { [key: string]: number };
}

interface GoogleAdsData {
  totalSpend: number;
  totalClicks: number;
  totalImpressions: number;
  ctr: number;
  cpc: number;
  roas: number;
  campaigns: {
    name: string;
    spend: number;
    clicks: number;
    impressions: number;
    conversions: number;
  }[];
  keywords?: {
    keyword: string;
    matchType: string;
    clicks: number;
    impressions: number;
    spend: number;
    conversions: number;
    ctr: number;
    cpc: number;
    qualityScore?: number;
  }[];
  demographics?: {
    ageRanges: { range: string; clicks: number; conversions: number }[];
    genders: { gender: string; clicks: number; conversions: number }[];
    incomeRanges: { range: string; clicks: number; conversions: number }[];
  };
  geographic?: {
    location: string;
    clicks: number;
    impressions: number;
    spend: number;
    conversions: number;
  }[];
  devices?: {
    device: string;
    clicks: number;
    impressions: number;
    spend: number;
    conversions: number;
    ctr: number;
  }[];
  searchTerms?: {
    searchTerm: string;
    clicks: number;
    impressions: number;
    spend: number;
    conversions: number;
    addedAsKeyword: boolean;
  }[];
}

interface AwningPricingRow {
  id: number;
  model_id: string;
  size_display_ft: string;
  size_ft_width: number;
  size_ft_projection: number;
  retail_price: number;
  install_cost: number;
  total_price: number;
  cost_price: number;
}

interface PergolaPricingRow {
  id: number;
  product_type: string;
  size_display_ft: string;
  size_ft_width: number;
  size_ft_depth: number;
  retail_price: number;
  install_cost: number;
  total_price: number;
  cost_price: number;
}

type PricingView = 'awnings' | 'pergolas';

type PricingRow = AwningPricingRow | PergolaPricingRow;

const AWNING_MODEL_ORDER = ['quick-shade', 'smart-shade', 'complete-shade', 'evie-luxe'] as const;

const AWNING_MODEL_LABELS: Record<(typeof AWNING_MODEL_ORDER)[number], string> = {
  'quick-shade': 'Quick Shade',
  'smart-shade': 'Smart Shade',
  'complete-shade': 'Complete Shade',
  'evie-luxe': 'Evie Luxe',
};

const AWNING_MODEL_COLORS: Record<(typeof AWNING_MODEL_ORDER)[number], string> = {
  'quick-shade': 'bg-sky-600',
  'smart-shade': 'bg-indigo-600',
  'complete-shade': 'bg-violet-600',
  'evie-luxe': 'bg-rose-600',
};

const PERGOLA_TYPE_ORDER = ['premium'] as const;

const PERGOLA_TYPE_LABELS: Record<(typeof PERGOLA_TYPE_ORDER)[number], string> = {
  premium: 'Premium Pergola',
};

const PERGOLA_TYPE_COLORS: Record<(typeof PERGOLA_TYPE_ORDER)[number], string> = {
  premium: 'bg-amber-600',
};

const BULK_PRICING_FIELD_LABELS: Record<BulkPricingField, string> = {
  retail_price: 'Retail price',
  install_cost: 'Install cost',
  cost_price: 'Cost price',
};

const BULK_PRICING_MODE_LABELS: Record<BulkPricingMode, string> = {
  amount: 'Dollar change',
  percent: 'Percent change',
  set: 'Set exact value',
};

const getPricingRowScopeValue = (row: PricingRow): string =>
  'model_id' in row ? row.model_id : row.product_type;

// Helper functions for lead display
const isToday = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

const isHotLead = (timeline?: string): boolean => {
  return timeline === 'asap' || timeline === '1-2months';
};

const getTimelineLabel = (timeline?: string): string => {
  switch (timeline) {
    case 'asap': return '🔥 ASAP';
    case '1-2months': return '⏰ 1-2 months';
    case '3-6months': return '📅 3-6 months';
    case 'planning': return '💭 Just planning';
    default: return '';
  }
};

const getProductTypeIcon = (source?: string, productType?: string): string => {
  if (source?.includes('pergola') || productType === 'pergola') return '🏛️';
  if (source?.includes('awning') || productType === 'awning') return '☀️';
  if (source?.includes('planter') || productType === 'planter') return '🌿';
  return '📋';
};

const getProductTypeLabel = (source?: string, productType?: string): string => {
  if (source?.includes('pergola') || productType === 'pergola') return 'Pergola';
  if (source?.includes('awning') || productType === 'awning') return 'Awning';
  if (source?.includes('planter') || productType === 'planter') return 'Planter';
  return 'Quote';
};

const AdminDashboard: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [authError, setAuthError] = useState('');
  const [quotes, setQuotes] = useState<QuoteSubmission[]>([]);
  const [financing, setFinancing] = useState<FinancingInquiry[]>([]);
  const [callbacks, setCallbacks] = useState<QuickCallback[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateFilter, setDateFilter] = useState('9999'); // All time by default
  const [marketingMetrics, setMarketingMetrics] = useState<MarketingMetrics | null>(null);
  const [googleAdsData, setGoogleAdsData] = useState<GoogleAdsData | null>(null);
  const [ga4Data, setGA4Data] = useState<any>(null);
  const [selectedLead, setSelectedLead] = useState<QuoteSubmission | null>(null);
  const [awningPricing, setAwningPricing] = useState<AwningPricingRow[]>([]);
  const [pergolaPricing, setPergolaPricing] = useState<PergolaPricingRow[]>([]);
  const [pricingView, setPricingView] = useState<PricingView>('awnings');
  const [bulkPricingScope, setBulkPricingScope] = useState('all');
  const [bulkPricingField, setBulkPricingField] = useState<BulkPricingField>('retail_price');
  const [bulkPricingMode, setBulkPricingMode] = useState<BulkPricingMode>('percent');
  const [bulkPricingAmount, setBulkPricingAmount] = useState('');
  const [pricingUpdateStatus, setPricingUpdateStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [invoicePayments, setInvoicePayments] = useState<InvoicePayment[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraft | null>(null);
  const [copiedFromInvoiceNumber, setCopiedFromInvoiceNumber] = useState<string | null>(null);
  const [invoiceStatusMessage, setInvoiceStatusMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [invoiceSetupError, setInvoiceSetupError] = useState('');
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    method: 'e-transfer',
    reference: '',
    notes: '',
  });

  const calculateMarketingMetrics = (quotes: QuoteSubmission[], financing: FinancingInquiry[], callbacks: QuickCallback[]) => {
    const totalLeads = quotes.length + financing.length + callbacks.length;
    const quotesWithValue = quotes.filter(q => q.total_with_hst && q.total_with_hst > 0);
    const conversionRate = totalLeads > 0 ? (quotesWithValue.length / totalLeads) * 100 : 0;
    const avgQuoteValue = quotesWithValue.length > 0
      ? quotesWithValue.reduce((sum, q) => sum + (q.total_with_hst || 0), 0) / quotesWithValue.length
      : 0;

    console.log('📊 REAL DATA SUMMARY:');
    console.log(`Total Leads: ${totalLeads}`);
    console.log(`Quotes with Values: ${quotesWithValue.length}`);
    console.log(`Conversion Rate: ${conversionRate.toFixed(2)}%`);
    console.log(`Average Quote Value: $${avgQuoteValue.toFixed(2)}`);

    // Source analysis
    const sourceMap = new Map<string, { count: number; value: number }>();

    quotes.forEach(quote => {
      const source = quote.source || 'Unknown';
      const existing = sourceMap.get(source) || { count: 0, value: 0 };
      sourceMap.set(source, {
        count: existing.count + 1,
        value: existing.value + (quote.total_with_hst || 0)
      });
    });

    financing.forEach(fin => {
      const source = 'Financing Request';
      const existing = sourceMap.get(source) || { count: 0, value: 0 };
      sourceMap.set(source, {
        count: existing.count + 1,
        value: existing.value + fin.total_amount
      });
    });

    callbacks.forEach(callback => {
      const source = callback.source || 'Quick Callback';
      const existing = sourceMap.get(source) || { count: 0, value: 0 };
      sourceMap.set(source, {
        count: existing.count + 1,
        value: existing.value
      });
    });

    const topSources = Array.from(sourceMap.entries())
      .map(([source, data]) => ({ source, count: data.count, value: data.value }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Daily leads for the last 7 days
    const dailyLeads = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayLeads = [...quotes, ...financing, ...callbacks].filter(item =>
        item.created_at.startsWith(dateStr)
      ).length;

      dailyLeads.push({
        date: date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
        count: dayLeads
      });
    }

    // Source breakdown for pie chart
    const sourceBreakdown: { [key: string]: number } = {};
    sourceMap.forEach((data, source) => {
      sourceBreakdown[source] = data.count;
    });

    setMarketingMetrics({
      totalLeads,
      conversionRate,
      avgQuoteValue,
      topSources,
      dailyLeads,
      sourceBreakdown
    });
  };

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(dateFilter));

      console.log('Loading data with date filter:', dateFilter, 'days ago:', daysAgo.toISOString());

      // Load quote submissions
      const { data: quotesData, error: quotesError } = await supabase
        .from('quote_submissions')
        .select('*')
        .gte('created_at', daysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (quotesError) {
        console.error('Error loading quote submissions:', quotesError);
      } else {
        console.log('Loaded quote submissions:', quotesData?.length || 0, 'records');
      }

      // Load financing inquiries
      const { data: financingData, error: financingError } = await supabase
        .from('financing_inquiries')
        .select('*')
        .gte('created_at', daysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (financingError) {
        console.error('Error loading financing inquiries:', financingError);
      }

      // Load quick callbacks
      const { data: callbacksData, error: callbacksError } = await supabase
        .from('quick_callbacks')
        .select('*')
        .gte('created_at', daysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (callbacksError) {
        console.error('Error loading callbacks:', callbacksError);
      }

      // Load invoices + payments (if configured)
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select('*')
        .gte('created_at', daysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (invoicesError) {
        console.error('Error loading invoices:', invoicesError);
        setInvoiceSetupError('Invoices are not set up yet. Run INVOICES_SUPABASE_SETUP.sql in Supabase to enable invoice creation and payment tracking.');
        setInvoices([]);
        setInvoicePayments([]);
      } else {
        const { data: paymentsData, error: paymentsError } = await supabase
          .from('invoice_payments')
          .select('*')
          .order('payment_date', { ascending: false });

        if (paymentsError) {
          console.error('Error loading invoice payments:', paymentsError);
          setInvoiceSetupError('Invoice payments could not be loaded. Run INVOICES_SUPABASE_SETUP.sql in Supabase if this is a new feature.');
          setInvoicePayments([]);
        } else {
          setInvoiceSetupError('');
          setInvoicePayments(((paymentsData || []) as any[]).map((payment) => ({
            ...payment,
            amount: Number(payment.amount) || 0,
          })));
        }

        setInvoices(((invoicesData || []) as any[]).map((invoice) => ({
          ...invoice,
          line_items: normalizeInvoiceLineItems(invoice.line_items),
          subtotal: Number(invoice.subtotal) || 0,
          tax_rate: Number(invoice.tax_rate) || 0.13,
          tax_amount: Number(invoice.tax_amount) || 0,
          total_amount: Number(invoice.total_amount) || 0,
          amount_paid: Number(invoice.amount_paid) || 0,
          balance_due: Number(invoice.balance_due) || 0,
          status: deriveInvoiceStatus({
            status: invoice.status,
            due_date: invoice.due_date,
            total_amount: Number(invoice.total_amount) || 0,
            amount_paid: Number(invoice.amount_paid) || 0,
            balance_due: Number(invoice.balance_due) || 0,
          }),
        })));
      }

      setQuotes(quotesData || []);
      setFinancing(financingData || []);
      setCallbacks(callbacksData || []);

      // Calculate marketing metrics
      calculateMarketingMetrics(quotesData || [], financingData || [], callbacksData || []);

      // Load Google Ads data
      try {
        const adsData = await fetchGoogleAdsData(dateFilter);
        setGoogleAdsData(adsData);
      } catch (error) {
        console.error('Error loading Google Ads data:', error);
      }

      // Load GA4 data
      try {
        const ga4Response = await fetchGA4Data(dateFilter);
        setGA4Data(ga4Response);
      } catch (error) {
        console.error('Error loading GA4 data:', error);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  // Check if user is authenticated and is admin on mount
  const checkAuth = React.useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // Check if user is admin
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('Error checking admin status:', error);
          setAuthError('Error checking admin status. Please contact support.');
          return;
        }

        if (profile?.is_admin) {
          setIsAuthenticated(true);
          loadData();
        } else {
          setAuthError('You do not have admin access. Please contact the administrator.');
          await supabase.auth.signOut();
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    }
  }, [loadData]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async () => {
    setAuthError('');
    setLoading(true);

    try {
      if (authMode === 'signup') {
        // Sign up new user
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          setAuthError('Account created! Please check your email to verify your account. After verification, contact the administrator to grant admin access.');
        }
      } else {
        // Login existing user
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (data.session?.user) {
          // Check if user is admin
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', data.session.user.id)
            .single();

          if (profileError) {
            console.error('Error checking admin status:', profileError);
            setAuthError('Error checking admin status. Please contact support.');
            await supabase.auth.signOut();
            return;
          }

          if (profile?.is_admin) {
            setIsAuthenticated(true);
            loadData();
          } else {
            setAuthError('You do not have admin access. Please contact the administrator.');
            await supabase.auth.signOut();
          }
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      setAuthError(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setEmail('');
    setPassword('');
  };

  const loadGoogleAdsData = React.useCallback(async () => {
    try {
      const adsData = await fetchGoogleAdsData(dateFilter);
      setGoogleAdsData(adsData);
    } catch (error) {
      console.error('Error loading Google Ads data:', error);
    }
  }, [dateFilter]);

  const loadGA4Data = React.useCallback(async () => {
    try {
      const ga4Response = await fetchGA4Data(dateFilter);
      setGA4Data(ga4Response);
    } catch (error) {
      console.error('Error loading GA4 data:', error);
    }
  }, [dateFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
      loadGoogleAdsData();
      loadGA4Data();
    }
  }, [dateFilter, isAuthenticated, loadData, loadGoogleAdsData, loadGA4Data]);

  const loadPricingData = async () => {
    const [{ data: awningData, error: awningError }, { error: pergolaError }] = await Promise.all([
      supabase.from('awning_pricing').select('*').eq('is_active', true).order('model_id').order('size_ft_width').order('size_ft_projection'),
      supabase.from('pergola_pricing').select('*').eq('is_active', true).order('product_type').order('size_ft_width').order('size_ft_depth'),
    ]);

    if (awningError) {
      throw new Error(awningError.message);
    }

    if (pergolaError) {
      throw new Error(pergolaError.message);
    }

    setAwningPricing(awningData || []);
    setPergolaPricing(PREMIUM_SIZES.map((item, index) => ({
      id: index + 1, product_type: 'premium',
      size_display_ft: item.size.replace('x', ' × ') + ' ft (approx.) · ' + item.widthM + ' × ' + item.depthM + ' × 3 m',
      size_ft_width: Number(item.size.split('x')[0]), size_ft_depth: Number(item.size.split('x')[1]),
      retail_price: item.priceCAD, install_cost: 0, total_price: item.priceCAD, cost_price: item.materialsCAD,
    })));
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchPricing = async () => {
      try {
        await loadPricingData();
      } catch (error) {
        console.error('Failed to load pricing data', error);
      }
    };
    void fetchPricing();
  }, [isAuthenticated]);

  useEffect(() => {
    setBulkPricingScope('all');
    setPricingUpdateStatus(null);
  }, [pricingView]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);

  const pricingScopeOptions = pricingView === 'awnings'
    ? [
        { value: 'all', label: 'All awnings' },
        ...AWNING_MODEL_ORDER.map(model => ({ value: model, label: AWNING_MODEL_LABELS[model] })),
      ]
    : [
        { value: 'all', label: 'All pergolas' },
        ...PERGOLA_TYPE_ORDER.map(type => ({ value: type, label: PERGOLA_TYPE_LABELS[type] })),
      ];

  const currentPricingRows: PricingRow[] = pricingView === 'awnings' ? awningPricing : pergolaPricing;

  const selectedPricingRows = currentPricingRows.filter(
    row => bulkPricingScope === 'all' || getPricingRowScopeValue(row) === bulkPricingScope,
  );

  const selectedPricingScopeLabel = pricingScopeOptions.find(option => option.value === bulkPricingScope)?.label
    || pricingScopeOptions[0].label;

  const handleBulkPricingUpdate = async () => {
    if (pricingView === 'pergolas') return; // Current prices are calculated from supplier costs.
    setPricingUpdateStatus(null);

    const adjustmentAmount = Number(bulkPricingAmount);
    if (bulkPricingAmount.trim() === '' || Number.isNaN(adjustmentAmount)) {
      setPricingUpdateStatus({ type: 'error', message: 'Enter a valid adjustment amount before applying a bulk update.' });
      return;
    }

    if (selectedPricingRows.length === 0) {
      setPricingUpdateStatus({ type: 'error', message: 'No pricing rows match the selected scope.' });
      return;
    }

    const adjustmentSummary = bulkPricingMode === 'percent'
      ? `${adjustmentAmount}%`
      : formatCurrency(adjustmentAmount);

    const confirmed = window.confirm(
      `Apply ${adjustmentSummary} to ${BULK_PRICING_FIELD_LABELS[bulkPricingField].toLowerCase()} for ${selectedPricingRows.length} rows in ${selectedPricingScopeLabel}?`,
    );

    if (!confirmed) {
      return;
    }

    const tableName = pricingView === 'awnings' ? 'awning_pricing' : 'pergola_pricing';
    const timestamp = new Date().toISOString();
    const updatesById = new Map(
      selectedPricingRows.map(row => [
        row.id,
        applyBulkPricingAdjustment(row, bulkPricingField, bulkPricingMode, adjustmentAmount),
      ]),
    );

    setIsSavingPricing(true);

    try {
      const results = await Promise.all(
        Array.from(updatesById.entries()).map(([id, update]) =>
          supabase
            .from(tableName)
            .update({ ...update, updated_at: timestamp })
            .eq('id', id),
        ),
      );

      const failedUpdate = results.find(result => result.error);
      if (failedUpdate?.error) {
        throw new Error(failedUpdate.error.message);
      }

      if (pricingView === 'awnings') {
        setAwningPricing(prev => prev.map(row => {
          const update = updatesById.get(row.id);
          return update ? { ...row, ...update } : row;
        }));
      } else {
        setPergolaPricing(prev => prev.map(row => {
          const update = updatesById.get(row.id);
          return update ? { ...row, ...update } : row;
        }));
      }

      setPricingUpdateStatus({
        type: 'success',
        message: `Updated ${selectedPricingRows.length} ${pricingView === 'awnings' ? 'awning' : 'pergola'} pricing rows.`,
      });
      setBulkPricingAmount('');
    } catch (error) {
      setPricingUpdateStatus({
        type: 'error',
        message: `Bulk update failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsSavingPricing(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  const invoiceNumbers = invoices.map((invoice) => invoice.invoice_number);

  const syncInvoiceRecord = (invoice: InvoiceRecord, payments: InvoicePayment[]): InvoiceRecord => {
    const lineItems = normalizeInvoiceLineItems(invoice.line_items);
    const totals = calculateInvoiceTotals(lineItems, Number(invoice.tax_rate) || 0.13, payments);
    return {
      ...invoice,
      line_items: lineItems,
      subtotal: totals.subtotal,
      tax_amount: totals.taxAmount,
      total_amount: totals.totalAmount,
      amount_paid: totals.amountPaid,
      balance_due: totals.balanceDue,
      status: deriveInvoiceStatus({
        status: invoice.status,
        due_date: invoice.due_date,
        total_amount: totals.totalAmount,
        amount_paid: totals.amountPaid,
        balance_due: totals.balanceDue,
      }),
    };
  };

  const getInvoiceBaseStatusBadgeClass = (status: string) => {
    const map: Record<string, string> = {
      draft: 'bg-slate-100 text-slate-800',
      sent: 'bg-blue-100 text-blue-800',
      partial: 'bg-amber-100 text-amber-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const openManualInvoiceForm = () => {
    setInvoiceStatusMessage(null);
    setEditingInvoiceId(null);
    setCopiedFromInvoiceNumber(null);
    setInvoiceDraft(buildManualInvoiceDraft(invoiceNumbers));
    setActiveTab('invoices');
  };

  const openQuoteInvoiceForm = (quote: QuoteSubmission) => {
    setInvoiceStatusMessage(null);
    setEditingInvoiceId(null);
    setCopiedFromInvoiceNumber(null);
    setInvoiceDraft(buildInvoiceDraftFromQuote(quote, invoiceNumbers));
    setSelectedLead(null);
    setActiveTab('invoices');
  };

  const openExistingInvoiceForm = (invoice: InvoiceRecord) => {
    setInvoiceStatusMessage(null);
    setEditingInvoiceId(invoice.id);
    setSelectedInvoiceId(invoice.id);
    setCopiedFromInvoiceNumber(null);
    setInvoiceDraft(buildInvoiceDraftFromInvoice(invoice));
    setActiveTab('invoices');
  };

  const openDuplicateInvoiceForm = (invoice: InvoiceRecord) => {
    setInvoiceStatusMessage(null);
    setEditingInvoiceId(null);
    setCopiedFromInvoiceNumber(invoice.invoice_number);
    setInvoiceDraft(buildInvoiceDraftFromExistingInvoice(invoice, invoiceNumbers));
    setActiveTab('invoices');
  };

  const closeInvoiceDraft = () => {
    setInvoiceDraft(null);
    setEditingInvoiceId(null);
    setCopiedFromInvoiceNumber(null);
  };

  const saveInvoiceDraft = async () => {
    if (!invoiceDraft) return;

    const existingPayments = editingInvoiceId
      ? getInvoicePayments(invoicePayments, editingInvoiceId)
      : [];

    const lineItems = invoiceDraft.line_items
      .map((item) => normalizeLineItem(item))
      .filter((item) => item.description.trim().length > 0 || item.total !== 0);

    if (!invoiceDraft.customer_name.trim()) {
      setInvoiceStatusMessage({ type: 'error', message: 'Customer name is required before saving an invoice.' });
      return;
    }

    if (lineItems.length === 0) {
      setInvoiceStatusMessage({ type: 'error', message: 'Add at least one invoice line item before saving.' });
      return;
    }

    setIsSavingInvoice(true);
    setInvoiceStatusMessage(null);

    try {
      const totals = calculateInvoiceTotals(lineItems, Number(invoiceDraft.tax_rate) || 0.13, existingPayments);
      const invoicePayload = {
        invoice_number: invoiceDraft.invoice_number.trim(),
        quote_submission_id: invoiceDraft.quote_submission_id,
        customer_name: invoiceDraft.customer_name.trim(),
        customer_email: invoiceDraft.customer_email.trim() || null,
        customer_phone: invoiceDraft.customer_phone.trim() || null,
        billing_street_address: invoiceDraft.billing_street_address.trim() || null,
        billing_city: invoiceDraft.billing_city.trim() || null,
        billing_province: invoiceDraft.billing_province.trim() || null,
        billing_postal_code: invoiceDraft.billing_postal_code.trim() || null,
        source: invoiceDraft.source,
        issue_date: invoiceDraft.issue_date,
        due_date: invoiceDraft.due_date || null,
        line_items: lineItems,
        subtotal: totals.subtotal,
        tax_rate: Number(invoiceDraft.tax_rate) || 0.13,
        tax_amount: totals.taxAmount,
        total_amount: totals.totalAmount,
        amount_paid: totals.amountPaid,
        balance_due: totals.balanceDue,
        status: deriveInvoiceStatus({
          status: invoiceDraft.status,
          due_date: invoiceDraft.due_date,
          total_amount: totals.totalAmount,
          amount_paid: totals.amountPaid,
          balance_due: totals.balanceDue,
        }),
        notes: invoiceDraft.notes.trim() || null,
        deposit_rate: invoiceDraft.payment_rates.depositRate,
        delivery_rate: invoiceDraft.payment_rates.deliveryRate,
        final_inspection_rate: invoiceDraft.payment_rates.finalInspectionRate,
      };

      const invoiceQuery = editingInvoiceId
        ? supabase.from('invoices').update({ ...invoicePayload, updated_at: new Date().toISOString() }).eq('id', editingInvoiceId)
        : supabase.from('invoices').insert([invoicePayload]);

      const { data, error } = await invoiceQuery
        .select()
        .single();

      if (error) {
        throw error;
      }

      const savedInvoice = syncInvoiceRecord({
        ...(data as any),
        line_items: normalizeInvoiceLineItems((data as any).line_items),
      } as InvoiceRecord, existingPayments);

      setInvoices((prev) => editingInvoiceId
        ? prev.map((current) => current.id === editingInvoiceId ? savedInvoice : current)
        : [savedInvoice, ...prev]);
      setSelectedInvoiceId(savedInvoice.id);
      closeInvoiceDraft();
      setInvoiceStatusMessage({
        type: 'success',
        message: editingInvoiceId
          ? `Invoice ${savedInvoice.invoice_number} updated successfully.`
          : `Invoice ${savedInvoice.invoice_number} created successfully.`,
      });
    } catch (error: any) {
      setInvoiceStatusMessage({ type: 'error', message: error.message || 'Failed to save invoice.' });
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const updateInvoiceStatus = async (invoice: InvoiceRecord, baseStatus: 'draft' | 'sent') => {
    const nextInvoice = syncInvoiceRecord({ ...invoice, status: baseStatus }, getInvoicePayments(invoicePayments, invoice.id));

    const { error } = await supabase
      .from('invoices')
      .update({ status: nextInvoice.status, updated_at: new Date().toISOString() })
      .eq('id', invoice.id);

    if (error) {
      setInvoiceStatusMessage({ type: 'error', message: error.message || 'Failed to update invoice status.' });
      return;
    }

    setInvoices((prev) => prev.map((current) => current.id === invoice.id ? nextInvoice : current));
  };

  const recordInvoicePayment = async () => {
    if (!selectedInvoiceId) return;

    const amount = Number(paymentDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setInvoiceStatusMessage({ type: 'error', message: 'Enter a valid payment amount greater than zero.' });
      return;
    }

    const currentInvoice = invoices.find((invoice) => invoice.id === selectedInvoiceId);
    if (!currentInvoice) return;

    setIsRecordingPayment(true);
    setInvoiceStatusMessage(null);

    try {
      const paymentPayload = {
        invoice_id: selectedInvoiceId,
        payment_date: paymentDraft.payment_date,
        amount,
        method: paymentDraft.method,
        reference: paymentDraft.reference.trim() || null,
        notes: paymentDraft.notes.trim() || null,
      };

      const { data, error } = await supabase
        .from('invoice_payments')
        .insert([paymentPayload])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const createdPayment = {
        ...(data as any),
        amount: Number((data as any).amount) || 0,
      } as InvoicePayment;

      const nextPayments = [createdPayment, ...invoicePayments];
      const syncedInvoice = syncInvoiceRecord(currentInvoice, getInvoicePayments(nextPayments, currentInvoice.id));

      const { error: invoiceUpdateError } = await supabase
        .from('invoices')
        .update({
          amount_paid: syncedInvoice.amount_paid,
          balance_due: syncedInvoice.balance_due,
          status: syncedInvoice.status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentInvoice.id);

      if (invoiceUpdateError) {
        throw invoiceUpdateError;
      }

      setInvoicePayments(nextPayments);
      setInvoices((prev) => prev.map((invoice) => invoice.id === currentInvoice.id ? syncedInvoice : invoice));
      setPaymentDraft({
        payment_date: new Date().toISOString().split('T')[0],
        amount: '',
        method: 'e-transfer',
        reference: '',
        notes: '',
      });
      setInvoiceStatusMessage({ type: 'success', message: `Recorded ${formatCurrency(amount)} payment for ${currentInvoice.invoice_number}.` });
    } catch (error: any) {
      setInvoiceStatusMessage({ type: 'error', message: error.message || 'Failed to record payment.' });
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const printInvoice = (invoice: InvoiceRecord) => {
    const html = buildInvoicePrintHtml(
      invoice,
      getInvoicePayments(invoicePayments, invoice.id),
      `${window.location.origin}/images/logo/evie_outdoors_transparent.png`,
    );

    const printFrame = document.createElement('iframe');
    printFrame.setAttribute('title', `print-${invoice.invoice_number}`);
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.visibility = 'hidden';

    const cleanupPrintFrame = () => {
      window.setTimeout(() => {
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 500);
    };

    printFrame.onload = () => {
      const frameWindow = printFrame.contentWindow;
      if (!frameWindow) {
        setInvoiceStatusMessage({ type: 'error', message: 'Unable to open the print preview for this invoice.' });
        cleanupPrintFrame();
        return;
      }

      frameWindow.onafterprint = cleanupPrintFrame;
      frameWindow.focus();
      window.setTimeout(() => {
        frameWindow.print();
      }, 300);
    };

    printFrame.srcdoc = html;
    document.body.appendChild(printFrame);
  };

  const getStatusBadgeClass = (status: string) => {
    const map: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      quoted: 'bg-purple-100 text-purple-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      approved: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const updateQuoteStatus = async (id: number, newStatus: string) => {
    await supabase.from('quote_submissions').update({ status: newStatus }).eq('id', id);
    setQuotes(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q));
    setSelectedLead(prev => (prev?.id === id ? { ...prev, status: newStatus } : prev));
  };

  const updateFinancingStatus = async (id: number, newStatus: string) => {
    await supabase.from('financing_inquiries').update({ status: newStatus }).eq('id', id);
    setFinancing(prev => prev.map(f => f.id === id ? { ...f, status: newStatus } : f));
  };

  const updateCallbackStatus = async (id: number, newStatus: string) => {
    await supabase.from('quick_callbacks').update({ status: newStatus }).eq('id', id);
    setCallbacks(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
  };

  const selectedInvoice = invoices.find((invoice) => invoice.id === selectedInvoiceId) || null;
  const selectedInvoicePayments = selectedInvoice ? getInvoicePayments(invoicePayments, selectedInvoice.id) : [];
  const selectedInvoiceRates = selectedInvoice ? getInvoicePaymentRates(selectedInvoice) : null;
  const selectedInvoiceScheduleLabels = selectedInvoiceRates ? buildScheduleLabels(selectedInvoiceRates) : null;
  const selectedInvoiceSchedule = selectedInvoice && selectedInvoiceRates
    ? calculateInvoicePaymentSchedule(selectedInvoice.total_amount, selectedInvoice.amount_paid, selectedInvoiceRates)
    : null;
  const invoiceTotalPaid = invoices.reduce((sum, invoice) => sum + (invoice.amount_paid || 0), 0);
  const invoiceOutstanding = invoices.reduce((sum, invoice) => sum + (invoice.balance_due || 0), 0);
  const isEditingExistingInvoice = editingInvoiceId !== null;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-center mb-6">Evie Outdoors Admin</h1>

          {authError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {authError}
            </div>
          )}

          <div className="space-y-4">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : authMode === 'login' ? 'Login' : 'Sign Up'}
            </button>

            <div className="text-center">
              <button
                onClick={() => {
                  setAuthMode(authMode === 'login' ? 'signup' : 'login');
                  setAuthError('');
                }}
                className="text-blue-600 hover:text-blue-700 text-sm"
                disabled={loading}
              >
                {authMode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Login'}
              </button>
            </div>

            {authMode === 'signup' && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                <strong>Note:</strong> After signing up, you'll need to verify your email and contact the administrator to grant admin access.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const totalQuoteValue = quotes.reduce((sum, quote) => sum + (quote.total_with_hst || 0), 0);

  // Pipeline & marketing computations (used by Marketing tab)
  const wonQuotes = quotes.filter(q => q.status === 'won');
  const wonRevenue = wonQuotes.reduce((sum, q) => sum + (q.total_with_hst || 0), 0);
  const avgDealSize = wonQuotes.length > 0 ? wonRevenue / wonQuotes.length : 0;
  const openQuotes = quotes.filter(q => !['won', 'lost'].includes(q.status || 'new'));
  const pipelineValue = openQuotes.reduce((sum, q) => sum + (q.total_with_hst || 0), 0);
  const winRate = quotes.length > 0 ? (wonQuotes.length / quotes.length) * 100 : 0;
  const contactedCount = quotes.filter(q => q.status === 'contacted').length;
  const pipelineStages = [
    { label: 'New', status: 'new', textColor: 'text-blue-700', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    { label: 'Contacted', status: 'contacted', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' },
    { label: 'Quoted', status: 'quoted', textColor: 'text-purple-700', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
    { label: 'Won ✓', status: 'won', textColor: 'text-green-700', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
    { label: 'Lost ✗', status: 'lost', textColor: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  ];
  const pipelineCounts = pipelineStages.map(s => ({
    ...s,
    count: quotes.filter(q => (q.status || 'new') === s.status).length,
    value: quotes.filter(q => (q.status || 'new') === s.status).reduce((sum, q) => sum + (q.total_with_hst || 0), 0),
  }));
  const monthlyTrend = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (5 - i));
    const year = d.getFullYear();
    const month = d.getMonth();
    const count = [...quotes, ...financing, ...callbacks].filter(item => {
      const itemDate = new Date(item.created_at);
      return itemDate.getFullYear() === year && itemDate.getMonth() === month;
    }).length;
    return { label: d.toLocaleDateString('en-CA', { month: 'short', year: '2-digit' }), count };
  });
  const maxMonthlyCount = Math.max(...monthlyTrend.map(m => m.count), 1);
  const pergolaQuotes = quotes.filter(q => q.pergola_model || q.product_type === 'pergola');
  const awningQuotes = quotes.filter(q => !q.pergola_model && (q.product_type === 'awning' || (q.source || '').toLowerCase().includes('awning')));
  const otherQuotes = quotes.filter(q => !q.pergola_model && q.product_type !== 'pergola' && q.product_type !== 'awning' && !(q.source || '').toLowerCase().includes('awning'));

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-gray-900">Evie Outdoors Admin Dashboard</h1>
            <div className="flex items-center gap-4">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="180">Last 6 months</option>
                <option value="365">Last year</option>
                <option value="9999">All time</option>
              </select>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'marketing', label: 'Marketing Analysis' },
              { id: 'analytics', label: 'Analytics Deep Dive' },
              { id: 'ads-setup', label: 'Google Ads Setup' },
              { id: 'quotes', label: 'Quote Submissions' },
              { id: 'financing', label: 'Financing Requests' },
              { id: 'callbacks', label: 'Quick Callbacks' },
              { id: 'invoices', label: 'Invoices' },
              { id: 'pricing', label: 'Pricing' },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-blue-600 rounded text-white flex items-center justify-center text-sm font-bold">Q</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Quote Submissions</p>
                        <p className="text-2xl font-bold text-gray-900">{quotes.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-green-600 rounded text-white flex items-center justify-center text-sm font-bold">$</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Quote Value</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalQuoteValue)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-purple-600 rounded text-white flex items-center justify-center text-sm font-bold">F</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Financing Requests</p>
                        <p className="text-2xl font-bold text-gray-900">{financing.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-orange-600 rounded text-white flex items-center justify-center text-sm font-bold">C</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Callback Requests</p>
                        <p className="text-2xl font-bold text-gray-900">{callbacks.length}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Recent Activity */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Recent Leads</h3>
                    <span className="text-sm text-gray-500">{quotes.length} total</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {quotes.slice(0, 8).map((quote) => (
                      <div
                        key={`quote-${quote.id}`}
                        className={`p-4 hover:bg-blue-50 transition-colors cursor-pointer ${isHotLead(quote.timeline) ? 'border-l-4 border-l-red-500' : ''}`}
                        onClick={() => setSelectedLead(quote)}
                      >
                        <div className="flex items-start justify-between">
                          {/* Left side - Lead info */}
                          <div className="flex-1 min-w-0">
                            {/* Header row with name and badges */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-lg">{getProductTypeIcon(quote.source, quote.product_type)}</span>
                              <span className="font-semibold text-gray-900">{quote.name || 'Unknown'}</span>
                              {isToday(quote.created_at) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  NEW
                                </span>
                              )}
                              {isHotLead(quote.timeline) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  🔥 HOT
                                </span>
                              )}
                            </div>

                            {/* Details row */}
                            <div className="mt-1 flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                              {quote.city && (
                                <span className="flex items-center gap-1">
                                  📍 {quote.city}
                                </span>
                              )}
                              {(quote.pergola_model || quote.pergola_size) && (
                                <span className="flex items-center gap-1">
                                  📦 {quote.pergola_model} {quote.pergola_size}
                                </span>
                              )}
                              {quote.timeline && (
                                <span>{getTimelineLabel(quote.timeline)}</span>
                              )}
                            </div>

                            {/* Message preview */}
                            {quote.message && (
                              <p className="mt-1 text-sm text-gray-500 truncate max-w-md">
                                💬 {quote.message.length > 60 ? quote.message.substring(0, 60) + '...' : quote.message}
                              </p>
                            )}

                            {/* Timestamp */}
                            <p className="mt-1 text-xs text-gray-400">
                              {formatDate(quote.created_at)}
                            </p>
                          </div>

                          {/* Right side - Quick actions */}
                          <div className="flex items-center gap-2 ml-4">
                            {quote.phone && (
                              <a
                                href={`tel:${quote.phone}`}
                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors"
                                title={`Call ${quote.phone}`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                              </a>
                            )}
                            {quote.email && (
                              <a
                                href={`mailto:${quote.email}`}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                title={`Email ${quote.email}`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </a>
                            )}
                            {quote.total_with_hst ? (
                              <span className="text-green-600 font-semibold text-sm whitespace-nowrap">
                                {formatCurrency(quote.total_with_hst)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {getProductTypeLabel(quote.source, quote.product_type)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {quotes.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <p>No leads yet. They'll appear here when customers submit quote requests.</p>
                      </div>
                    )}
                  </div>

                  {quotes.length > 8 && (
                    <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                      <button
                        onClick={() => setActiveTab('quotes')}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View all {quotes.length} leads →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Marketing Analysis Tab */}
            {activeTab === 'marketing' && marketingMetrics && (
              <div className="space-y-6">
                {/* Sales Pipeline Funnel */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Sales Pipeline</h3>
                  <p className="text-sm text-gray-500 mb-4">Based on your actual quote statuses</p>
                  <div className="grid grid-cols-5 gap-3">
                    {pipelineCounts.map((stage, i) => (
                      <div key={i} className={`rounded-lg border p-4 text-center ${stage.bgColor} ${stage.borderColor}`}>
                        <p className={`text-3xl font-bold ${stage.textColor}`}>{stage.count}</p>
                        <p className={`text-sm font-semibold mt-1 ${stage.textColor}`}>{stage.label}</p>
                        <p className="text-xs text-gray-500 mt-1">{formatCurrency(stage.value)}</p>
                      </div>
                    ))}
                  </div>
                  {quotes.length > 0 && (
                    <div className="mt-4 flex items-center gap-3">
                      <span className="text-sm text-gray-500">Win rate</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(winRate, 100)}%` }}></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">{winRate.toFixed(1)}%</span>
                    </div>
                  )}
                </div>

                {/* Revenue Snapshot */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-green-600 rounded text-white flex items-center justify-center text-sm font-bold">$</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Won Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(wonRevenue)}</p>
                        <p className="text-xs text-gray-500 mt-1">{wonQuotes.length} deal{wonQuotes.length !== 1 ? 's' : ''} closed</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-blue-600 rounded text-white flex items-center justify-center text-sm font-bold">P</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Pipeline Value</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(pipelineValue)}</p>
                        <p className="text-xs text-gray-500 mt-1">{openQuotes.length} open quote{openQuotes.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-purple-600 rounded text-white flex items-center justify-center text-sm font-bold">A</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Avg Deal Size (Won)</p>
                        <p className="text-2xl font-bold text-gray-900">{wonQuotes.length > 0 ? formatCurrency(avgDealSize) : '—'}</p>
                        <p className="text-xs text-gray-500 mt-1">from won quotes only</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-indigo-600 rounded text-white flex items-center justify-center text-sm font-bold">L</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Leads</p>
                        <p className="text-2xl font-bold text-gray-900">{marketingMetrics.totalLeads}</p>
                        <p className="text-xs text-gray-500 mt-1">quotes + financing + callbacks</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-blue-600 rounded text-white flex items-center justify-center text-sm font-bold">$</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Avg Quote Value</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(marketingMetrics.avgQuoteValue)}</p>
                        <p className="text-xs text-gray-500 mt-1">across all submitted quotes</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-green-600 rounded text-white flex items-center justify-center text-sm font-bold">W</div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Win Rate</p>
                        <p className="text-2xl font-bold text-gray-900">{quotes.length > 0 ? `${winRate.toFixed(1)}%` : '—'}</p>
                        <p className="text-xs text-gray-500 mt-1">{wonQuotes.length} won of {quotes.length} quotes</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Monthly Lead Trend */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Lead Trend</h3>
                    <div className="space-y-2">
                      {monthlyTrend.map((month, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 w-12 shrink-0">{month.label}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                            <div
                              className="bg-indigo-500 h-5 rounded-full flex items-center justify-end pr-2 transition-all"
                              style={{ width: `${(month.count / maxMonthlyCount) * 100}%` }}
                            >
                              {month.count > 0 && <span className="text-xs text-white font-medium">{month.count}</span>}
                            </div>
                          </div>
                          {month.count === 0 && <span className="text-xs text-gray-400">0</span>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Lead Sources */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Top Lead Sources</h3>
                    <div className="space-y-3">
                      {marketingMetrics.topSources.length > 0 ? marketingMetrics.topSources.map((source, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-gray-900">{source.source}</p>
                            <p className="text-sm text-gray-600">{source.count} leads</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">{formatCurrency(source.value)}</p>
                            <p className="text-sm text-gray-600">{source.count > 0 ? formatCurrency(source.value / source.count) : '$0'} avg</p>
                          </div>
                        </div>
                      )) : (
                        <p className="text-sm text-gray-400 text-center py-4">No source data available</p>
                      )}
                    </div>
                  </div>

                  {/* Product Mix */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Product Mix</h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Pergolas', qs: pergolaQuotes, color: 'bg-blue-500' },
                        { label: 'Awnings', qs: awningQuotes, color: 'bg-green-500' },
                        { label: 'Other / Unclassified', qs: otherQuotes, color: 'bg-gray-400' },
                      ].filter(p => p.qs.length > 0).map((product, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-900">{product.label}</span>
                            <span className="text-gray-600">{product.qs.length} · {formatCurrency(product.qs.reduce((s, q) => s + (q.total_with_hst || 0), 0))}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-3">
                            <div
                              className={`${product.color} h-3 rounded-full`}
                              style={{ width: `${quotes.length > 0 ? (product.qs.length / quotes.length) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                      {quotes.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">No quote data available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pipeline Insights */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pipeline Insights</h3>
                  <div className="space-y-3">
                    {quotes.length === 0 && financing.length === 0 && callbacks.length === 0 && (
                      <div className="flex items-start p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="h-5 w-5 bg-blue-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">i</div>
                        <div className="ml-3">
                          <p className="font-medium text-blue-800">No leads yet</p>
                          <p className="text-sm text-blue-700">Leads will appear here once customers submit quotes, financing requests, or callbacks.</p>
                        </div>
                      </div>
                    )}
                    {quotes.length > 0 && wonQuotes.length === 0 && (
                      <div className="flex items-start p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="h-5 w-5 bg-yellow-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">!</div>
                        <div className="ml-3">
                          <p className="font-medium text-yellow-800">No Won Deals Yet</p>
                          <p className="text-sm text-yellow-700">You have {quotes.length} quote{quotes.length !== 1 ? 's' : ''} in your pipeline — mark one as "Won ✓" when a sale closes to start tracking revenue.</p>
                        </div>
                      </div>
                    )}
                    {winRate > 0 && winRate < 20 && (
                      <div className="flex items-start p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="h-5 w-5 bg-orange-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">!</div>
                        <div className="ml-3">
                          <p className="font-medium text-orange-800">Win Rate Below 20%</p>
                          <p className="text-sm text-orange-700">Currently winning {winRate.toFixed(1)}% of quotes. Follow up faster after sending quotes — most deals are won or lost within 48 hours.</p>
                        </div>
                      </div>
                    )}
                    {winRate >= 30 && wonQuotes.length >= 2 && (
                      <div className="flex items-start p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="h-5 w-5 bg-green-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">✓</div>
                        <div className="ml-3">
                          <p className="font-medium text-green-800">Strong Win Rate!</p>
                          <p className="text-sm text-green-700">Closing {winRate.toFixed(1)}% of quotes — above average for home improvement. Great job converting leads.</p>
                        </div>
                      </div>
                    )}
                    {contactedCount > 5 && (
                      <div className="flex items-start p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="h-5 w-5 bg-blue-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">i</div>
                        <div className="ml-3">
                          <p className="font-medium text-blue-800">Follow-Up Backlog</p>
                          <p className="text-sm text-blue-700">{contactedCount} leads are still in "Contacted" status. Push them to "Quoted" or follow up to move the needle.</p>
                        </div>
                      </div>
                    )}
                    {pipelineValue > 50000 && (
                      <div className="flex items-start p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="h-5 w-5 bg-purple-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">$</div>
                        <div className="ml-3">
                          <p className="font-medium text-purple-800">Strong Pipeline</p>
                          <p className="text-sm text-purple-700">{formatCurrency(pipelineValue)} in active open quotes. Keep following up — even closing half of these would be significant revenue.</p>
                        </div>
                      </div>
                    )}
                    {marketingMetrics.totalLeads < 10 && (
                      <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="h-5 w-5 bg-red-400 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">!</div>
                        <div className="ml-3">
                          <p className="font-medium text-red-800">Low Lead Volume</p>
                          <p className="text-sm text-red-700">Only {marketingMetrics.totalLeads} leads in the selected period. Adjust the date filter or consider expanding your marketing channels.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Deep Dive Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* GA4 Overview */}
                {ga4Data && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Google Analytics 4 Overview</h3>
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Mock Data - Setup GA4 API for Real Data
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{ga4Data.sessions.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">Sessions</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{ga4Data.users.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-purple-600">{(ga4Data.conversionRate * 100).toFixed(2)}%</p>
                        <p className="text-sm text-gray-600">Conversion Rate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-orange-600">{formatCurrency(ga4Data.revenue)}</p>
                        <p className="text-sm text-gray-600">Revenue</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Traffic Sources & Keywords */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* GA4 Traffic Sources */}
                  {ga4Data && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Traffic Sources (GA4)</h3>
                      <div className="space-y-3">
                        {ga4Data.trafficSources.map((source: any, index: number) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium text-gray-900">{source.source} / {source.medium}</p>
                              {source.campaign && <p className="text-sm text-gray-600">{source.campaign}</p>}
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{source.sessions} sessions</p>
                              <p className="text-sm text-gray-600">{source.conversions} conversions</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Google Ads Keywords */}
                  {googleAdsData?.keywords && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Top Keywords (Google Ads)</h3>
                      <div className="space-y-3">
                        {googleAdsData.keywords.map((keyword, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium text-gray-900">{keyword.keyword}</p>
                              <p className="text-sm text-gray-600">{keyword.matchType} • QS: {keyword.qualityScore}/10</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{formatCurrency(keyword.spend)}</p>
                              <p className="text-sm text-gray-600">{keyword.conversions} conv • {keyword.ctr.toFixed(2)}% CTR</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Demographics & Geographic */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Age Demographics */}
                  {googleAdsData?.demographics && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Age Demographics</h3>
                      <div className="space-y-2">
                        {googleAdsData.demographics.ageRanges.map((age, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">{age.range}</span>
                            <div className="flex items-center">
                              <div
                                className="bg-blue-500 h-3 rounded mr-2"
                                style={{ width: `${Math.max(age.clicks / 2, 4)}px` }}
                              ></div>
                              <span className="text-sm font-medium">{age.clicks}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geographic Performance */}
                  {googleAdsData?.geographic && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Geographic Performance</h3>
                      <div className="space-y-3">
                        {googleAdsData.geographic.map((geo, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-900">{geo.location}</p>
                              <p className="text-sm text-gray-600">{geo.clicks} clicks</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{formatCurrency(geo.spend)}</p>
                              <p className="text-sm text-gray-600">{geo.conversions} conv</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Device Performance */}
                  {googleAdsData?.devices && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Device Performance</h3>
                      <div className="space-y-3">
                        {googleAdsData.devices.map((device, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-900">{device.device}</p>
                              <p className="text-sm text-gray-600">{device.ctr.toFixed(2)}% CTR</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{formatCurrency(device.spend)}</p>
                              <p className="text-sm text-gray-600">{device.conversions} conv</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Search Terms Analysis */}
                {googleAdsData?.searchTerms && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Search Terms Analysis</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Search Term</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spend</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added as Keyword</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {googleAdsData.searchTerms.map((term, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {term.searchTerm}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {term.clicks}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(term.spend)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {term.conversions}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  term.addedAsKeyword
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {term.addedAsKeyword ? 'Added' : 'Opportunity'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Google Ads Setup Tab */}
            {activeTab === 'ads-setup' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Google Ads API Setup</h3>
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Current Status</h4>
                    <p className="text-sm text-blue-700">
                      {googleAdsData ?
                        "✅ Google Ads data is loading (currently using mock data)" :
                        "❌ Google Ads API not configured"
                      }
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Setup Instructions</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap">{SETUP_INSTRUCTIONS}</pre>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Environment Variables</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Create a <code className="bg-gray-100 px-2 py-1 rounded">.env</code> file in your react-app folder with these variables:
                    </p>
                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                      <div>REACT_APP_GOOGLE_ADS_CLIENT_ID=your_client_id_here</div>
                      <div>REACT_APP_GOOGLE_ADS_CLIENT_SECRET=your_client_secret_here</div>
                      <div>REACT_APP_GOOGLE_ADS_REFRESH_TOKEN=your_refresh_token_here</div>
                      <div>REACT_APP_GOOGLE_ADS_CUSTOMER_ID=your_customer_id_here</div>
                      <div>REACT_APP_GOOGLE_ADS_DEVELOPER_TOKEN=your_developer_token_here</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Quick Links</h4>
                    <div className="space-y-2">
                      <a
                        href="https://developers.google.com/google-ads/api"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-blue-600 hover:text-blue-800 text-sm"
                      >
                        📚 Google Ads API Documentation
                      </a>
                      <a
                        href="https://console.developers.google.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-blue-600 hover:text-blue-800 text-sm"
                      >
                        🔧 Google Cloud Console
                      </a>
                      <a
                        href="https://ads.google.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-blue-600 hover:text-blue-800 text-sm"
                      >
                        📊 Google Ads Account
                      </a>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-800 mb-2">Important Notes</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Google Ads API requires approval for production use</li>
                      <li>• Start with test/sandbox environment first</li>
                      <li>• Keep your API credentials secure and never commit them to git</li>
                      <li>• The current implementation shows mock data until API is configured</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Quote Submissions Tab */}
            {activeTab === 'quotes' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Quote Submissions ({quotes.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {quotes.map((quote) => (
                        <tr key={quote.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedLead(quote)}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{quote.name}</div>
                              <div className="text-sm text-gray-500">{quote.email}</div>
                              <div className="text-sm text-gray-500">{quote.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {quote.pergola_model && `${quote.pergola_model} ${quote.pergola_size || ''}`}
                              {quote.source?.includes('awning') && 'Awning'}
                              {quote.source?.includes('planter') && 'Planter'}
                            </div>
                            <div className="text-sm text-gray-500">{quote.source}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quote.total_with_hst ? formatCurrency(quote.total_with_hst) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={quote.status || 'new'}
                              onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                              className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${getStatusBadgeClass(quote.status || 'new')}`}
                            >
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="quoted">Quoted</option>
                              <option value="won">Won ✓</option>
                              <option value="lost">Lost ✗</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(quote.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Financing Requests Tab */}
            {activeTab === 'financing' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Financing Requests ({financing.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Financing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {financing.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{req.name}</div>
                              <div className="text-sm text-gray-500">{req.email}</div>
                              <div className="text-sm text-gray-500">{req.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(req.total_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(req.financing_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={req.status || 'pending'}
                              onChange={(e) => updateFinancingStatus(req.id, e.target.value)}
                              className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${getStatusBadgeClass(req.status || 'pending')}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="approved">Approved ✓</option>
                              <option value="declined">Declined ✗</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(req.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Quick Callbacks Tab */}
            {activeTab === 'callbacks' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Quick Callback Requests ({callbacks.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Best Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {callbacks.map((callback) => (
                        <tr key={callback.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <a href={`tel:${callback.phone}`} className="text-blue-600 hover:text-blue-800">
                              {callback.phone}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {callback.best_time}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {callback.source}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={callback.status || 'pending'}
                              onChange={(e) => updateCallbackStatus(callback.id, e.target.value)}
                              className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${getStatusBadgeClass(callback.status || 'pending')}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="completed">Completed ✓</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(callback.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Invoices</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{invoices.length}</p>
                    <p className="text-xs text-gray-500 mt-1">Manual + quote-generated</p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Payments Received</p>
                    <p className="text-2xl font-bold text-green-700 mt-2">{formatCurrency(invoiceTotalPaid)}</p>
                    <p className="text-xs text-gray-500 mt-1">Across all invoices</p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Outstanding</p>
                    <p className="text-2xl font-bold text-amber-700 mt-2">{formatCurrency(invoiceOutstanding)}</p>
                    <p className="text-xs text-gray-500 mt-1">Remaining balance due</p>
                  </div>
                </div>

                {invoiceSetupError && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-800">Invoice setup required</p>
                    <p className="text-sm text-yellow-700 mt-1">{invoiceSetupError}</p>
                  </div>
                )}

                <div className="bg-white rounded-lg shadow p-6 space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Invoice workflow</h3>
                      <p className="text-sm text-gray-500">Create invoices manually or from a quote, then record payments as they come in.</p>
                    </div>
                    <button
                      onClick={openManualInvoiceForm}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      + New Manual Invoice
                    </button>
                  </div>

                  {invoiceStatusMessage && (
                    <div className={`rounded-lg px-4 py-3 text-sm ${invoiceStatusMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {invoiceStatusMessage.message}
                    </div>
                  )}

                  {invoiceDraft && (
                    <div className="border border-blue-100 rounded-xl p-5 bg-blue-50/40 space-y-5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{isEditingExistingInvoice ? `Edit Invoice ${invoiceDraft.invoice_number}` : copiedFromInvoiceNumber ? `New Invoice ${invoiceDraft.invoice_number}` : 'Create Invoice'}</h4>
                          <p className="text-sm text-gray-500">PDF-ready invoice with the Evie logo and payment tracking.</p>
                        </div>
                        <button
                          onClick={closeInvoiceDraft}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>

                      {isEditingExistingInvoice && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          You are editing an existing invoice. Saved payments will stay attached and the balance will be recalculated automatically.
                        </div>
                      )}

                      {!isEditingExistingInvoice && copiedFromInvoiceNumber && (
                        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                          Copied customer details and line items from {copiedFromInvoiceNumber}. This saves as a brand new draft invoice — payments on {copiedFromInvoiceNumber} are not carried over.
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Invoice #</label>
                          <input
                            value={invoiceDraft.invoice_number}
                            onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, invoice_number: e.target.value } : prev)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Issue Date</label>
                          <input
                            type="date"
                            value={invoiceDraft.issue_date}
                            onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, issue_date: e.target.value } : prev)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Due Date</label>
                          <input
                            type="date"
                            value={invoiceDraft.due_date}
                            onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, due_date: e.target.value } : prev)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Base Status</label>
                          <select
                            value={invoiceDraft.status}
                            onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, status: e.target.value as 'draft' | 'sent' } : prev)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          >
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Customer Name</label>
                          <input value={invoiceDraft.customer_name} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, customer_name: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Email</label>
                          <input value={invoiceDraft.customer_email} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, customer_email: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Phone</label>
                          <input value={invoiceDraft.customer_phone} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, customer_phone: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Tax Rate</label>
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              value={Math.round(invoiceDraft.tax_rate * 10000) / 100}
                              onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, tax_rate: Number(e.target.value) / 100 } : prev)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            />
                            <span className="absolute right-3 top-2 text-gray-400 text-sm">%</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Street Address</label>
                          <input value={invoiceDraft.billing_street_address} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, billing_street_address: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">City</label>
                          <input value={invoiceDraft.billing_city} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, billing_city: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Province</label>
                          <input value={invoiceDraft.billing_province} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, billing_province: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Postal Code</label>
                          <input value={invoiceDraft.billing_postal_code} onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, billing_postal_code: e.target.value } : prev)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-xs font-semibold text-gray-500 uppercase">Line Items</label>
                          <button
                            onClick={() => setInvoiceDraft(prev => prev ? { ...prev, line_items: [...prev.line_items, emptyInvoiceLineItem()] } : prev)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            + Add line item
                          </button>
                        </div>

                        <div className="space-y-3">
                          {invoiceDraft.line_items.map((item, index) => {
                            const normalized = normalizeLineItem(item);
                            return (
                              <div key={index} className="grid grid-cols-12 gap-3 items-end bg-white border border-gray-200 rounded-lg p-3">
                                <div className="col-span-12 md:col-span-5">
                                  <label className="block text-xs text-gray-500 mb-1">Description</label>
                                  <input
                                    value={item.description}
                                    onChange={(e) => setInvoiceDraft(prev => prev ? {
                                      ...prev,
                                      line_items: prev.line_items.map((current, currentIndex) => currentIndex === index ? { ...current, description: e.target.value } : current),
                                    } : prev)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  />
                                </div>
                                <div className="col-span-4 md:col-span-2">
                                  <label className="block text-xs text-gray-500 mb-1">Qty</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={item.quantity}
                                    onChange={(e) => setInvoiceDraft(prev => prev ? {
                                      ...prev,
                                      line_items: prev.line_items.map((current, currentIndex) => currentIndex === index ? { ...current, quantity: Number(e.target.value) } : current),
                                    } : prev)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  />
                                </div>
                                <div className="col-span-4 md:col-span-2">
                                  <label className="block text-xs text-gray-500 mb-1">Unit Price</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.unitPrice}
                                    onChange={(e) => setInvoiceDraft(prev => prev ? {
                                      ...prev,
                                      line_items: prev.line_items.map((current, currentIndex) => currentIndex === index ? { ...current, unitPrice: Number(e.target.value) } : current),
                                    } : prev)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  />
                                </div>
                                <div className="col-span-4 md:col-span-2">
                                  <label className="block text-xs text-gray-500 mb-1">Amount</label>
                                  <div className="h-10 px-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center text-sm font-semibold text-gray-800">
                                    {formatCurrency(normalized.total)}
                                  </div>
                                </div>
                                <div className="col-span-12 md:col-span-1 flex md:justify-end">
                                  <button
                                    onClick={() => setInvoiceDraft(prev => prev ? {
                                      ...prev,
                                      line_items: prev.line_items.length === 1
                                        ? [emptyInvoiceLineItem()]
                                        : prev.line_items.filter((_, currentIndex) => currentIndex !== index),
                                    } : prev)}
                                    className="text-sm text-red-600 hover:text-red-800"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Notes</label>
                        <textarea
                          rows={3}
                          value={invoiceDraft.notes}
                          onChange={(e) => setInvoiceDraft(prev => prev ? { ...prev, notes: e.target.value } : prev)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder="Optional notes or terms for the customer"
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={saveInvoiceDraft}
                          disabled={isSavingInvoice}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400"
                        >
                          {isSavingInvoice ? 'Saving...' : isEditingExistingInvoice ? 'Save Changes' : 'Save Invoice'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="text-lg font-medium text-gray-900">Invoices ({invoices.length})</h3>
                    <p className="text-sm text-gray-500">Click any invoice to see its details, print it, or record a payment.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedInvoiceId(invoice.id)}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-semibold text-gray-900">{invoice.invoice_number}</div>
                              <div className="text-xs text-gray-500">{invoice.quote_submission_id ? `Quote #${invoice.quote_submission_id}` : 'Manual invoice'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{invoice.customer_name}</div>
                              <div className="text-xs text-gray-500">{invoice.customer_email || '—'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.total_amount)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-700 font-medium">{formatCurrency(invoice.amount_paid)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-700">{formatCurrency(invoice.balance_due)}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getInvoiceBaseStatusBadgeClass(String(invoice.status))}`}>
                                {String(invoice.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.due_date || '—'}</td>
                          </tr>
                        ))}
                        {invoices.length === 0 && (
                          <tr>
                            <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                              No invoices yet. Create one manually or open any quote and use <span className="font-medium">Create Invoice</span>.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {selectedInvoice && (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2 bg-white rounded-lg shadow p-6 space-y-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-xl font-semibold text-gray-900">{selectedInvoice.invoice_number}</h3>
                            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getInvoiceBaseStatusBadgeClass(String(selectedInvoice.status))}`}>
                              {String(selectedInvoice.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">Issued {selectedInvoice.issue_date} • Due {selectedInvoice.due_date || '—'}</p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => openExistingInvoiceForm(selectedInvoice)} className="px-3 py-2 border border-amber-200 bg-amber-50 rounded-lg text-sm font-medium text-amber-800 hover:bg-amber-100">Edit Invoice</button>
                          <button onClick={() => openDuplicateInvoiceForm(selectedInvoice)} className="px-3 py-2 border border-green-200 bg-green-50 rounded-lg text-sm font-medium text-green-800 hover:bg-green-100">Duplicate</button>
                          <button onClick={() => updateInvoiceStatus(selectedInvoice, 'draft')} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Mark Draft</button>
                          <button onClick={() => updateInvoiceStatus(selectedInvoice, 'sent')} className="px-3 py-2 border border-blue-200 bg-blue-50 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100">Mark Sent</button>
                          <button onClick={() => printInvoice(selectedInvoice)} className="px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-black">Print / PDF</button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Customer</p>
                          <p className="font-medium text-gray-900">{selectedInvoice.customer_name}</p>
                          <p>{selectedInvoice.customer_email || '—'}</p>
                          <p>{selectedInvoice.customer_phone || '—'}</p>
                          <p className="mt-2 text-gray-600">{[selectedInvoice.billing_street_address, selectedInvoice.billing_city, selectedInvoice.billing_province, selectedInvoice.billing_postal_code].filter(Boolean).join(', ') || '—'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Totals</p>
                          <div className="space-y-2">
                            <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">HST ({Math.round((selectedInvoice.tax_rate || 0) * 100)}%)</span><span className="font-medium">{formatCurrency(selectedInvoice.tax_amount)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Total Contract Value</span><span className="font-medium">{formatCurrency(selectedInvoice.total_amount)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Paid</span><span className="font-medium text-green-700">{formatCurrency(selectedInvoice.amount_paid)}</span></div>
                            {selectedInvoiceSchedule && selectedInvoiceScheduleLabels && (
                              <>
                                <div className="flex justify-between"><span className="text-gray-600">Due Today ({selectedInvoiceScheduleLabels.deposit})</span><span className="font-semibold text-blue-700">{formatCurrency(selectedInvoiceSchedule.amountDueToday)}</span></div>
                                <div className="flex justify-between text-xs text-gray-500"><span>{selectedInvoiceScheduleLabels.delivery} on delivery</span><span>{formatCurrency(selectedInvoiceSchedule.deliveryDue)}</span></div>
                                <div className="flex justify-between text-xs text-gray-500"><span>{selectedInvoiceScheduleLabels.finalInspection} on final inspection</span><span>{formatCurrency(selectedInvoiceSchedule.finalInspectionDue)}</span></div>
                              </>
                            )}
                            <div className="flex justify-between pt-2 border-t"><span className="font-semibold">Remaining Balance</span><span className="font-bold text-amber-700">{formatCurrency(selectedInvoice.balance_due)}</span></div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Invoice Items</p>
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {normalizeInvoiceLineItems(selectedInvoice.line_items).map((item, index) => (
                                <tr key={`${selectedInvoice.id}-${index}`}>
                                  <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">{item.quantity}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.unitPrice)}</td>
                                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.total)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Notes</p>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                          {selectedInvoice.notes || 'No notes added.'}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6 space-y-5">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">Payments</h4>
                        <p className="text-sm text-gray-500">Record offline payments received for this invoice.</p>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Payment Date</label>
                          <input type="date" value={paymentDraft.payment_date} onChange={(e) => setPaymentDraft(prev => ({ ...prev, payment_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Amount</label>
                          <input type="number" step="0.01" value={paymentDraft.amount} onChange={(e) => setPaymentDraft(prev => ({ ...prev, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Method</label>
                          <select value={paymentDraft.method} onChange={(e) => setPaymentDraft(prev => ({ ...prev, method: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                            <option value="e-transfer">E-transfer</option>
                            <option value="cash">Cash</option>
                            <option value="credit-card">Credit Card</option>
                            <option value="debit">Debit</option>
                            <option value="other">Other</option>
                          </select>
                          <p className="mt-2 text-xs text-gray-500">E-transfer payments should be sent to <span className="font-medium text-gray-700">payments@evieoutdoors.com</span>.</p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Reference</label>
                          <input value={paymentDraft.reference} onChange={(e) => setPaymentDraft(prev => ({ ...prev, reference: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Optional confirmation #" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Notes</label>
                          <textarea rows={3} value={paymentDraft.notes} onChange={(e) => setPaymentDraft(prev => ({ ...prev, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                        </div>
                        <button onClick={recordInvoicePayment} disabled={isRecordingPayment} className="w-full bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400">
                          {isRecordingPayment ? 'Saving Payment...' : 'Record Payment'}
                        </button>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Payment History</p>
                        <div className="space-y-3">
                          {selectedInvoicePayments.map((payment) => (
                            <div key={payment.id} className="border border-gray-200 rounded-lg p-3">
                              <div className="flex justify-between gap-3">
                                <div>
                                  <p className="font-medium text-gray-900">{payment.method}</p>
                                  <p className="text-sm text-gray-500">{payment.payment_date}{payment.reference ? ` • ${payment.reference}` : ''}</p>
                                </div>
                                <p className="font-semibold text-green-700">{formatCurrency(payment.amount)}</p>
                              </div>
                              {payment.notes && <p className="text-sm text-gray-600 mt-2">{payment.notes}</p>}
                            </div>
                          ))}
                          {selectedInvoicePayments.length === 0 && (
                            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
                              No payments recorded yet.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">Pricing & Profit Analysis</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPricingView('awnings')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pricingView === 'awnings' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                    >
                      ☀️ Awnings
                    </button>
                    <button
                      onClick={() => setPricingView('pergolas')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pricingView === 'pergolas' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                    >
                      🏛️ Pergolas
                    </button>
                  </div>
                </div>
                {pricingView === 'awnings' && <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Bulk Update Pricing</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Apply one change across multiple rows in the current pricing view.
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      Matching rows: <span className="font-semibold text-gray-900">{selectedPricingRows.length}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-4">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">Apply to</span>
                      <select
                        value={bulkPricingScope}
                        onChange={(e) => setBulkPricingScope(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {pricingScopeOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">Update field</span>
                      <select
                        value={bulkPricingField}
                        onChange={(e) => setBulkPricingField(e.target.value as BulkPricingField)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {Object.entries(BULK_PRICING_FIELD_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">Change type</span>
                      <select
                        value={bulkPricingMode}
                        onChange={(e) => setBulkPricingMode(e.target.value as BulkPricingMode)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {Object.entries(BULK_PRICING_MODE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">Amount</span>
                      <input
                        type="number"
                        step="0.01"
                        value={bulkPricingAmount}
                        onChange={(e) => setBulkPricingAmount(e.target.value)}
                        placeholder={bulkPricingMode === 'percent' ? '5' : '1500'}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleBulkPricingUpdate}
                      disabled={isSavingPricing || bulkPricingAmount.trim() === ''}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                    >
                      {isSavingPricing ? 'Saving...' : 'Apply bulk update'}
                    </button>
                    <p className="text-xs text-gray-500">
                      Use a negative number to reduce values. Retail $/% updates snap to psych pricing, install $/% updates snap to clean $100 steps, and total sell price is recalculated automatically when retail or install changes.
                    </p>
                  </div>
                  {pricingUpdateStatus && (
                    <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${pricingUpdateStatus.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {pricingUpdateStatus.message}
                    </div>
                  )}
                </div>}
                {pricingView === 'pergolas' && <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-900">
                  <p>Current website catalogue: Greenawn freestanding motorized, under 10 units, 3 m height.</p>
                  <p>Old supplier USD × {OLD_QUOTE_FACTOR} provisional adjustment × {PERGOLA_FX} CAD/USD × {PERGOLA_MARKUP}. Screens: current US$ {SCREEN_USD_PER_FOOT}/linear ft × {PERGOLA_FX} × {OPTION_MARKUP}.</p>
                  <p>Standard installation and delivery included; special site work and unpriced options quoted separately. Premium+ is individually quoted.</p>
                  <p>Calculated from the approved supplier catalogue, independently of legacy database prices. The amount remaining after materials must still cover freight, labour, delivery and overhead.</p>
                </div>}
                {/* Legend */}
                <div className="flex gap-6 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-200 inline-block"></span>≥40% margin</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-yellow-200 inline-block"></span>30–40% margin</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-200 inline-block"></span>&lt;30% margin</span>
                </div>
                {/* Awnings View */}
                {pricingView === 'awnings' && (
                  <>
                    {AWNING_MODEL_ORDER.map(model => {
                      const rows = awningPricing.filter(r => r.model_id === model);
                      if (!rows.length) return null;
                      return (
                        <div key={model} className="bg-white rounded-lg shadow overflow-hidden">
                          <div className={`${AWNING_MODEL_COLORS[model]} px-6 py-3`}>
                            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">{AWNING_MODEL_LABELS[model]}</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  {['Size', 'Retail', 'Install', 'Total (Sell)', 'Cost', 'Profit $', 'Margin %'].map(h => (
                                    <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Size' ? 'text-left' : 'text-right'}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {rows.map(row => {
                                  const profit = row.total_price - row.cost_price;
                                  const margin = row.total_price > 0 ? (profit / row.total_price) * 100 : 0;
                                  const rowBg = margin < 30 ? 'bg-red-50' : 'hover:bg-gray-50';
                                  return (
                                    <tr key={row.id} className={rowBg}>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.size_display_ft}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(row.retail_price)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(row.install_cost)}</td>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(row.total_price)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(row.cost_price)}</td>
                                      <td className="px-4 py-3 text-sm font-bold text-green-700 text-right">{formatCurrency(profit)}</td>
                                      <td className="px-4 py-3 text-sm text-right">
                                        <span className={`font-semibold ${margin >= 40 ? 'text-green-600' : margin >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                                          {margin.toFixed(1)}%
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* Pergolas View */}
                {pricingView === 'pergolas' && (
                  <>
                    {PERGOLA_TYPE_ORDER.map(type => {
                      const rows = pergolaPricing.filter(r => r.product_type === type);
                      if (!rows.length) return null;
                      return (
                        <div key={type} className="bg-white rounded-lg shadow overflow-hidden">
                          <div className={`${PERGOLA_TYPE_COLORS[type]} px-6 py-3`}>
                            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">{PERGOLA_TYPE_LABELS[type]}</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  {['Size', 'Installed CAD', 'Install', 'Total CAD', 'Materials CAD', 'After materials', 'After materials %'].map(h => (
                                    <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Size' ? 'text-left' : 'text-right'}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {rows.map(row => {
                                  const profit = row.total_price - row.cost_price;
                                  const margin = row.total_price > 0 ? (profit / row.total_price) * 100 : 0;
                                  const rowBg = margin < 30 ? 'bg-red-50' : 'hover:bg-gray-50';
                                  return (
                                    <tr key={row.id} className={rowBg}>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.size_display_ft}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(row.retail_price)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-right">Included</td>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(row.total_price)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(row.cost_price)}</td>
                                      <td className="px-4 py-3 text-sm font-bold text-green-700 text-right">{formatCurrency(profit)}</td>
                                      <td className="px-4 py-3 text-sm text-right">
                                        <span className={`font-semibold ${margin >= 40 ? 'text-green-600' : margin >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                                          {margin.toFixed(1)}%
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-2xl">{getProductTypeIcon(selectedLead.source, selectedLead.product_type)}</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedLead.name || 'Unknown'}</h2>
                  <p className="text-sm text-gray-500">{formatDate(selectedLead.created_at)}</p>
                </div>
                {isToday(selectedLead.created_at) && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">NEW</span>
                )}
                {isHotLead(selectedLead.timeline) && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">🔥 HOT</span>
                )}
              </div>
              <button onClick={() => setSelectedLead(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-2">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Selector */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Lead Status</h3>
                <div className="flex gap-2 flex-wrap">
                  {(['new', 'contacted', 'quoted', 'won', 'lost'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateQuoteStatus(selectedLead.id, s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        (selectedLead.status || 'new') === s
                          ? `${getStatusBadgeClass(s)} border-transparent ring-2 ring-offset-1 ring-gray-400`
                          : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {s === 'new' ? 'New' : s === 'contacted' ? 'Contacted' : s === 'quoted' ? 'Quoted' : s === 'won' ? 'Won ✓' : 'Lost ✗'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <a href={`tel:${selectedLead.phone}`} className="text-blue-600 hover:text-blue-800 font-medium">{selectedLead.phone || '—'}</a>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:text-blue-800 font-medium break-all">{selectedLead.email || '—'}</a>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="font-medium text-gray-900">
                      {[selectedLead.street_address, selectedLead.city, selectedLead.province, selectedLead.postal_code].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              {(selectedLead.pergola_model || selectedLead.pergola_size || selectedLead.pergola_color || selectedLead.pergola_location) && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Product Details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedLead.pergola_model && (
                      <div><p className="text-xs text-gray-500">Model</p><p className="font-medium text-gray-900 capitalize">{selectedLead.pergola_model}</p></div>
                    )}
                    {selectedLead.pergola_size && (
                      <div><p className="text-xs text-gray-500">Size</p><p className="font-medium text-gray-900">{selectedLead.pergola_size}</p></div>
                    )}
                    {selectedLead.pergola_color && (
                      <div><p className="text-xs text-gray-500">Color</p><p className="font-medium text-gray-900 capitalize">{selectedLead.pergola_color}</p></div>
                    )}
                    {selectedLead.pergola_location && (
                      <div><p className="text-xs text-gray-500">Location</p><p className="font-medium text-gray-900 capitalize">{selectedLead.pergola_location}</p></div>
                    )}
                  </div>
                  {selectedLead.selected_addons && selectedLead.selected_addons.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">Add-ons</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedLead.selected_addons.map((addon, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">{addon}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pricing Breakdown */}
              {selectedLead.total_with_hst && selectedLead.total_with_hst > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Pricing Breakdown</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    {!!selectedLead.base_price && <div className="flex justify-between"><span className="text-gray-600">Base Price</span><span className="font-medium">{formatCurrency(selectedLead.base_price)}</span></div>}
                    {!!selectedLead.addons_total && selectedLead.addons_total > 0 && <div className="flex justify-between"><span className="text-gray-600">Add-ons</span><span className="font-medium">{formatCurrency(selectedLead.addons_total)}</span></div>}
                    {!!selectedLead.install_cost && selectedLead.install_cost > 0 && <div className="flex justify-between"><span className="text-gray-600">Installation</span><span className="font-medium">{formatCurrency(selectedLead.install_cost)}</span></div>}
                    {!!selectedLead.delivery_fee && selectedLead.delivery_fee > 0 && <div className="flex justify-between"><span className="text-gray-600">Delivery</span><span className="font-medium">{formatCurrency(selectedLead.delivery_fee)}</span></div>}
                    {!!selectedLead.fall_sale_discount && selectedLead.fall_sale_discount > 0 && <div className="flex justify-between"><span className="text-gray-600">Discount</span><span className="font-medium text-green-600">-{formatCurrency(selectedLead.fall_sale_discount)}</span></div>}
                    {!!selectedLead.subtotal && <div className="flex justify-between border-t pt-2"><span className="text-gray-600">Subtotal</span><span className="font-medium">{formatCurrency(selectedLead.subtotal)}</span></div>}
                    {!!selectedLead.hst && <div className="flex justify-between"><span className="text-gray-600">HST</span><span className="font-medium">{formatCurrency(selectedLead.hst)}</span></div>}
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total (with HST)</span>
                      <span className="text-green-600">{formatCurrency(selectedLead.total_with_hst)}</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Lead Details */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Lead Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedLead.timeline && (
                    <div><p className="text-xs text-gray-500">Timeline</p><p className="font-medium text-gray-900">{getTimelineLabel(selectedLead.timeline)}</p></div>
                  )}
                  {selectedLead.budget && (
                    <div><p className="text-xs text-gray-500">Budget</p><p className="font-medium text-gray-900">{selectedLead.budget}</p></div>
                  )}
                  {selectedLead.source && (
                    <div><p className="text-xs text-gray-500">Source</p><p className="font-medium text-gray-900">{selectedLead.source}</p></div>
                  )}
                  {selectedLead.referrer && (
                    <div className="col-span-2"><p className="text-xs text-gray-500">Referrer</p><p className="font-medium text-gray-900 break-all text-sm">{selectedLead.referrer}</p></div>
                  )}
                </div>
                {selectedLead.message && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Message</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded p-3 whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t bg-gray-50 flex gap-3 flex-wrap">
              {selectedLead.phone && (
                <a href={`tel:${selectedLead.phone}`} className="flex-1 text-center bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors min-w-[120px]">
                  📞 Call {selectedLead.phone}
                </a>
              )}
              {selectedLead.email && (
                <a href={`mailto:${selectedLead.email}`} className="flex-1 text-center bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors min-w-[120px]">
                  ✉️ Email
                </a>
              )}
              <button onClick={() => openQuoteInvoiceForm(selectedLead)} className="px-4 py-2 border border-blue-200 bg-blue-50 rounded-lg font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                Create Invoice
              </button>
              <button onClick={() => setSelectedLead(null)} className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
